import { get } from './base'
import { get as getStrict } from './base-strict'
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import moment from 'moment'
import Tag from '../models/tag'
import { convertToCamelCase } from '@/models/utils/casing'
import axios, { AxiosResponse } from 'axios'
import { ref, Ref, unref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { PaginatedRequest } from '@/models/search-location-dto'

const tagsEndpoint = '/api/v3/tags'
const tagsV4Endpoint = '/api/v4/tags'

export function getPaginatedTags(
  offset = 0,
  limit = 1000,
  signal: AbortSignal | undefined
): Promise<AxiosResponse> {
  const source = axios.CancelToken.source()
  const queryParams = new URLSearchParams()
  queryParams.append('offset', offset.toString())
  queryParams.append('limit', limit.toString())
  return getStrict(`${tagsV4Endpoint}?${queryParams.toString()}`, {
    cancelToken: source.token,
    signal,
  })
}

function getTags() {
  return get(tagsEndpoint)
}

export function useGetTagsQuery() {
  const { data, error, isLoading } = useQuery<Tag[]>({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await getTags()
      return response.data.map((tagData) => new Tag(tagData))
    },
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
  })

  return { data, error, isLoading }
}

type SearchTagCategoriesOptions = {
  searchTerm?: string | null
  orgId?: string | null
  hubId?: string | null
}

type SearchTagValuesOptions = {
  searchTerm?: string | null
  category?: string | null
  orgId?: string | null
  hubId?: string | null
}

interface SearchTagsPaginationOptions {
  offset?: number
  limit?: number
}

export function searchTagCategories(
  options: SearchTagCategoriesOptions,
  paginationOptions: SearchTagsPaginationOptions,
  signal?: AbortSignal | null
) {
  const source = axios.CancelToken.source()
  const queryParams = new URLSearchParams()
  if (options.searchTerm) {
    queryParams.append('searchTerm', options.searchTerm)
  }
  if (options.orgId) {
    queryParams.append('orgId', options.orgId)
  }
  if (options.hubId) {
    queryParams.append('hubId', options.hubId)
  }
  if (paginationOptions.offset) {
    queryParams.append('offset', paginationOptions.offset.toString())
  }

  if (paginationOptions.limit) {
    queryParams.append('limit', paginationOptions.limit.toString())
  }
  const tagCategories = get<PaginatedRequest<string>>(
    `${tagsV4Endpoint}/categories?${queryParams.toString()}`,
    {
      cancelToken: source.token,
    }
  ).then((response) => convertToCamelCase(response.data))
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return tagCategories
}

export function searchTagValues(
  options: SearchTagValuesOptions,
  paginationOptions: SearchTagsPaginationOptions,
  signal?: AbortSignal
) {
  const source = axios.CancelToken.source()
  const queryParams = new URLSearchParams()
  if (options.searchTerm) {
    queryParams.append('searchTerm', options.searchTerm)
  }
  if (options.category) {
    queryParams.append('category', options.category)
  }
  if (options.orgId) {
    queryParams.append('orgId', options.orgId)
  }
  if (options.hubId) {
    queryParams.append('hubId', options.hubId)
  }
  if (paginationOptions.offset) {
    queryParams.append('offset', paginationOptions.offset.toString())
  }

  if (paginationOptions.limit) {
    queryParams.append('limit', paginationOptions.limit.toString())
  }
  const tagCategories = get<PaginatedRequest<string>>(
    `${tagsV4Endpoint}/values?${queryParams.toString()}`,
    {
      cancelToken: source.token,
    }
  ).then((response) => convertToCamelCase(response.data))
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return tagCategories
}
const initialPageParam = { limit: 50, offset: 0 }
export function useSearchTagCategories(
  options: Ref<SearchTagCategoriesOptions>
) {
  const { queryKey } = useDebouncedQueryKeyBuilderForTagCategories(options)

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ signal, pageParam }) => {
      return searchTagCategories(
        unref(options),
        pageParam || initialPageParam,
        signal
      )
    },
    keepPreviousData: true,
    getNextPageParam: (previousPage) => {
      if (previousPage.meta.currentPage < previousPage.meta.totalPages) {
        return {
          limit: previousPage.meta.pageSize,
          offset: previousPage.meta.currentPage * previousPage.meta.pageSize,
        }
      }
    },
    // We want this query to never go stale, so it never re-fetches.
    // Otherwise we risk some UI jank if the query returns data from the cache,
    // but refetches in the background. If the locations on the server have
    // changed, location items may get bumped around in the list. The
    // user also may have paginated a hundred times, and we don't want to
    // re-execute all of those requests if we dont have to.
    //
    // The cache will be cleared by default 5 minutes after all components
    // using this query unmount.
    staleTime: Infinity,
    refetchOnMount: false,
  })
}

export function useSearchTagValues(options: Ref<SearchTagValuesOptions>) {
  const { queryKey } = useDebouncedQueryKeyBuilderForTagValues(options)

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ signal, pageParam }) => {
      return searchTagValues(
        unref(options),
        pageParam || initialPageParam,
        signal
      )
    },
    keepPreviousData: true,
    getNextPageParam: (previousPage) => {
      if (previousPage.meta.currentPage < previousPage.meta.totalPages) {
        return {
          limit: previousPage.meta.pageSize,
          offset: previousPage.meta.currentPage * previousPage.meta.pageSize,
        }
      }
    },
    // We want this query to never go stale, so it never re-fetches.
    // Otherwise we risk some UI jank if the query returns data from the cache,
    // but refetches in the background. If the locations on the server have
    // changed, location items may get bumped around in the list. The
    // user also may have paginated a hundred times, and we don't want to
    // re-execute all of those requests if we dont have to.
    //
    // The cache will be cleared by default 5 minutes after all components
    // using this query unmount.
    staleTime: Infinity,
    refetchOnMount: false,
  })
}

/**
 * So far I've found the best way to debounce queries run by tanstack is to
 * debounce the key. The query key changing is what causes the refetch.
 * We need a little more sophistication however in that we only want
 * to debounce the user typing
 */
function useDebouncedQueryKeyBuilderForTagCategories(
  options: Ref<SearchTagCategoriesOptions>
) {
  const queryKey = ref<any[]>(['tags', 'search', 'categories', unref(options)])

  const updateQueryKey = (newOptionsValue: SearchTagCategoriesOptions) => {
    queryKey.value = ['tags', 'search', 'categories', newOptionsValue]
  }

  const debouncedUpdate = useDebounceFn(updateQueryKey, 200)

  watch(options, (newOptions, oldOptions) => {
    if (newOptions.searchTerm !== oldOptions.searchTerm) {
      debouncedUpdate(newOptions)
    }
  })

  return { queryKey }
}

function useDebouncedQueryKeyBuilderForTagValues(
  options: Ref<SearchTagValuesOptions>
) {
  const queryKey = ref<any[]>(['tags', 'search', 'values', unref(options)])

  const updateQueryKey = (newOptionsValue: SearchTagValuesOptions) => {
    queryKey.value = ['tags', 'search', 'values', newOptionsValue]
  }

  const debouncedUpdate = useDebounceFn(updateQueryKey, 200)

  watch(options, (newOptions, oldOptions) => {
    if (
      newOptions.searchTerm !== oldOptions.searchTerm ||
      newOptions.category !== oldOptions.category
    ) {
      debouncedUpdate(newOptions)
    }
  })

  return { queryKey }
}
